import { useRef, useEffect, useCallback } from "react";
import { create } from "zustand";

export const RECONNECT_DELAY_MS = 4000;

export type OnlineState = {
  isOnline: boolean;
  isReconnecting: boolean;
  setOnline: (v: boolean) => void;
  setReconnecting: (v: boolean) => void;
};

export const useOnlineStore = create<OnlineState>((set) => ({
  isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
  isReconnecting: false,
  setOnline: (v) => set({ isOnline: v }),
  setReconnecting: (v) => set({ isReconnecting: v }),
}));

export function useOnline() {
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOnline = useOnlineStore((s) => s.isOnline);
  const isReconnecting = useOnlineStore((s) => s.isReconnecting);

  const clearTimer = useCallback(() => {
    if (reconnectTimer.current !== null) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
  }, []);

  useEffect(() => {
    const setStateIfChanged = (
      partial: Partial<Pick<OnlineState, "isOnline" | "isReconnecting">>
    ) => {
      const current = useOnlineStore.getState();
      const patch: Partial<OnlineState> = {};
      let changed = false;

      if (
        partial.isOnline !== undefined &&
        partial.isOnline !== current.isOnline
      ) {
        patch.isOnline = partial.isOnline;
        changed = true;
      }
      if (
        partial.isReconnecting !== undefined &&
        partial.isReconnecting !== current.isReconnecting
      ) {
        patch.isReconnecting = partial.isReconnecting;
        changed = true;
      }

      if (changed) useOnlineStore.setState(patch);
    };

    const current = useOnlineStore.getState();
    if (
      current.isOnline !== navigator.onLine ||
      current.isReconnecting !== false
    ) {
      setStateIfChanged({ isOnline: navigator.onLine, isReconnecting: false });
    }

    const handleOnline = () => {
      setStateIfChanged({ isReconnecting: true });
      clearTimer();

      reconnectTimer.current = setTimeout(() => {
        setStateIfChanged({ isOnline: true, isReconnecting: false });
        reconnectTimer.current = null;
      }, RECONNECT_DELAY_MS);
    };

    const handleOffline = () => {
      clearTimer();
      setStateIfChanged({ isOnline: false, isReconnecting: false });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearTimer();
    };
  }, [clearTimer]);

  return { isOnline, isReconnecting };
}
